import { ref } from 'vue'
import { BUNDLE_TERMS_VERSION, isBundleStale } from '@/legal/termsBundle'
import { useTermsStore } from '@/stores/terms'
import type { SwitchTerms } from '@/types'

export interface SwitchTermsBody {
  consent_version: string
  terms_accepted?: boolean
}

/**
 * The terms declaration a plan switch carries. While the contract's owner has
 * terms to accept the switch takes the tick, and the button waits for it.
 * Plain functions rather than computeds, as HostingSiteDetail needs.
 */
export function useSwitchTerms(terms: () => SwitchTerms | null) {
  const store = useTermsStore()
  const accepted = ref(false)
  // A newer version than the bundled text is published, so the declaration
  // would name text no longer offered; the page has to reload first.
  const stale = ref(false)

  const required = () => terms()?.needs_acceptance === true
  const ready = () => !stale.value && (!required() || accepted.value)

  const checkVersion = async () => {
    accepted.value = false
    await store.loadConfig()
    if (store.config && isBundleStale(store.config)) stale.value = true
  }

  const markStale = () => {
    stale.value = true
    void store.loadConfig(true)
  }

  const reloadForTerms = () => window.location.reload()

  const body = (): SwitchTermsBody => ({
    consent_version: BUNDLE_TERMS_VERSION,
    ...(required() ? { terms_accepted: accepted.value } : {})
  })

  return { accepted, stale, ready, checkVersion, markStale, reloadForTerms, body }
}
